
import { motion } from 'framer-motion';

interface HeroSectionProps {
  onRegisterClick: () => void;
  isScrolled: boolean;
}

export default function HeroSection({ onRegisterClick, isScrolled }: HeroSectionProps) {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Contact Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-2 md:px-6 py-1.5 md:py-3 flex flex-wrap items-center justify-between gap-1.5 md:gap-4">
          <div className="flex items-center gap-2 md:gap-6">
            <img 
              src="img/logo.png" 
              alt="PainMed логотип" 
              className="h-5 md:h-8 w-auto"
            />
            <a href="mailto:info@painmed.ru" className="hidden md:flex items-center gap-2 text-sm text-gray-700 hover:text-[#D8B36A] transition-colors cursor-pointer">
              <i className="ri-mail-line text-[#D8B36A]"></i>
              <span>info@painmed.ru</span>
            </a>
            <a href="tel:+78126605866" className="flex items-center gap-1.5 text-xs md:text-sm text-gray-700 hover:text-[#D8B36A] transition-colors cursor-pointer">
              <i className="ri-phone-line text-[#D8B36A] text-sm md:text-base"></i>
              <span className="hidden md:inline">+7 (812) 660-58-66</span>
            </a>
          </div>
          
          <div className="flex items-center gap-1.5 md:gap-4">
            <div className="flex items-center gap-1 md:gap-3">
              <a href="https://vk.com/protivboli" target="_blank" rel="noopener noreferrer" className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center text-gray-700 hover:text-[#D8B36A] transition-colors cursor-pointer">
                <i className="ri-vk-line text-base md:text-xl"></i>
              </a>
              <a href="https://vkvideo.ru/@protivboli" target="_blank" rel="noopener noreferrer" className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center text-gray-700 hover:text-[#D8B36A] transition-colors cursor-pointer">
                <i className="ri-video-line text-base md:text-xl"></i>
              </a>
              <a href="https://www.youtube.com/@%D0%A1%D0%97%D0%9E%D0%98%D0%91" target="_blank" rel="noopener noreferrer" className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center text-gray-700 hover:text-[#D8B36A] transition-colors cursor-pointer">
                <i className="ri-youtube-line text-base md:text-xl"></i>
              </a>
            </div>
            <button
              onClick={onRegisterClick}
              className="px-2.5 py-1 md:px-6 md:py-2 bg-[#D8B36A] text-[#101A2E] rounded-lg font-semibold text-[10px] md:text-sm hover:bg-[#C9A459] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Зарегистрироваться
            </button>
          </div>
        </div>
      </div>

      <div className="absolute inset-0">
        {/* Desktop image */}
        <img
          src="img/banner.png"
          alt="Петербуржские вечера - коморбидная патология"
          className="hidden md:block w-full h-full object-cover object-center"
        />
        {/* Mobile image */}
        <img
          src="img/banner_mob.png"
          alt="Петербуржские вечера - коморбидная патология"
          className="block md:hidden w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#101A2E]/50 via-transparent to-transparent"></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center -mt-[70px] md:-mt-[400px] lg:-mt-[350px] xl:-mt-[400px] pt-16 md:pt-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-7xl font-bold text-white mb-3 md:mb-6 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Петербуржские вечера
          </h1>
          
          <p className="text-lg md:text-3xl text-white/95 mb-4 md:mb-8 font-medium">
            2-я Школа коморбидной патологии
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 mb-4 md:mb-8 text-white/90 text-xs md:text-base">
            <div className="flex items-center gap-1 md:gap-2">
              <i className="ri-calendar-line text-[#D8B36A] text-base md:text-xl"></i>
              <span className="font-medium">12 февраля 2026</span>
            </div>
            <div className="flex items-center gap-1 md:gap-2">
              <i className="ri-time-line text-[#D8B36A] text-base md:text-xl"></i>
              <span className="font-medium">16:00–20:00</span>
            </div>
            <div className="flex items-center gap-1 md:gap-2">
              <i className="ri-map-pin-line text-[#D8B36A] text-base md:text-xl"></i>
              <span className="font-medium">отель «Москва», зал «Белые ночи»</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-5 md:mb-10">
            <span className="px-3 py-1.5 md:px-4 md:py-2 bg-white/10 backdrop-blur-md rounded-full text-white text-xs md:text-sm font-medium border border-white/20">
              Небольшой зал
            </span>
            <span className="px-3 py-1.5 md:px-4 md:py-2 bg-white/10 backdrop-blur-md rounded-full text-white text-xs md:text-sm font-medium border border-white/20">
              Без онлайн
            </span>
            <span className="px-3 py-1.5 md:px-4 md:py-2 bg-white/10 backdrop-blur-md rounded-full text-white text-xs md:text-sm font-medium border border-white/20">
              Тёплая обстановка
            </span>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-4 md:mb-6 mt-[150px] md:mt-0">
            <button
              onClick={onRegisterClick}
              className="px-6 py-3 md:px-8 md:py-4 bg-[#D8B36A] text-[#101A2E] rounded-lg font-bold text-sm md:text-base hover:bg-[#C9A459] transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Зарегистрироваться
            </button>
            <a
              href="https://painmed.ru/event/vtoraya-shkola-komorbidnoj-patologii-peterburzhskie-vechera/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 md:px-8 md:py-4 bg-white/10 backdrop-blur-md text-white rounded-lg font-semibold text-sm md:text-base hover:bg-white/20 transition-all duration-300 border border-white/30 whitespace-nowrap cursor-pointer"
            >
              Подробная программа
            </a>
          </div>

          <p className="text-white/80 text-xs md:text-sm font-medium">
            <i className="ri-information-line mr-1"></i>
            Места ограничены: бронируйте заранее
          </p>
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-white text-3xl"></i>
      </div>
    </section>
  );
}
